import { useState, useEffect, useCallback } from "react";
import AdminLayout from "@/pages/admin/components/AdminLayout";
import { supabase } from "@/lib/supabase";

const statusColors: Record<string, string> = {
  New: "bg-amber-100 text-amber-700",
  Reviewed: "bg-teal-100 text-teal-700",
  Accepted: "bg-emerald-100 text-emerald-700",
  Rejected: "bg-red-100 text-red-600",
};

const statusOptions = ["New", "Reviewed", "Accepted", "Rejected"];

export default function AdminInternsVolunteersPage() {
  const [applications, setApplications] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState("All");
  const [filterStatus, setFilterStatus] = useState("All");
  const [search, setSearch] = useState("");
  const [viewApp, setViewApp] = useState<any | null>(null);

  const fetchApplications = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("applications")
      .select("*")
      .order("date", { ascending: false });
    if (error) {
      console.error("Error fetching applications:", error);
    } else {
      setApplications(data || []);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchApplications();
  }, [fetchApplications]);

  const filtered = applications.filter((a) => {
    const matchType = filterType === "All" || a.type === filterType;
    const matchStatus = filterStatus === "All" || a.status === filterStatus;
    const matchSearch =
      a.name.toLowerCase().includes(search.toLowerCase()) ||
      (a.area || "").toLowerCase().includes(search.toLowerCase()) ||
      (a.country || "").toLowerCase().includes(search.toLowerCase());
    return matchType && matchStatus && matchSearch;
  });

  const updateStatus = async (id: string, status: string) => {
    const { error } = await supabase
      .from("applications")
      .update({ status, updated_at: new Date().toISOString() })
      .eq("id", id);
    if (error) {
      console.error("Error updating status:", error);
      return;
    }
    fetchApplications();
    if (viewApp?.id === id) {
      setViewApp((prev: any) => (prev ? { ...prev, status } : null));
    }
  };

  const formatDate = (d: string) => {
    if (!d) return "";
    return new Date(d).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const counts = {
    total: applications.length,
    internships: applications.filter((a) => a.type === "Internship").length,
    volunteers: applications.filter((a) => a.type === "Volunteer").length,
    newApps: applications.filter((a) => a.status === "New").length,
  };

  return (
    <AdminLayout title="Interns & Volunteers" subtitle="Review and manage all internship and volunteer applications">
      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
        {[
          { label: "Total Applications", value: counts.total, icon: "ri-file-list-3-line", color: "bg-stone-100 text-stone-600" },
          { label: "Internships", value: counts.internships, icon: "ri-graduation-cap-line", color: "bg-amber-50 text-amber-600" },
          { label: "Volunteers", value: counts.volunteers, icon: "ri-heart-line", color: "bg-rose-50 text-rose-600" },
          { label: "New (Unreviewed)", value: counts.newApps, icon: "ri-notification-badge-line", color: "bg-emerald-50 text-emerald-600" },
        ].map((s) => (
          <div key={s.label} className="bg-white rounded-2xl p-4 border border-stone-100">
            <div className={`w-9 h-9 flex items-center justify-center rounded-xl mb-3 ${s.color}`}>
              <i className={`${s.icon} text-base`}></i>
            </div>
            <p className="text-xl font-bold text-stone-800">{s.value}</p>
            <p className="text-xs text-stone-500 mt-0.5">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 mb-6">
        <div className="relative flex-1 w-full sm:max-w-xs">
          <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center">
            <i className="ri-search-line text-stone-400 text-sm"></i>
          </div>
          <input
            type="text"
            placeholder="Search applicants..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-4 py-2.5 rounded-xl border border-stone-200 bg-white text-sm focus:outline-none focus:border-primary text-stone-700 placeholder-stone-400"
          />
        </div>
        <select value={filterType} onChange={(e) => setFilterType(e.target.value)} className="px-3 py-2.5 rounded-xl border border-stone-200 bg-white text-sm text-stone-600 focus:outline-none focus:border-primary cursor-pointer">
          <option>All</option>
          <option>Internship</option>
          <option>Volunteer</option>
        </select>
        <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="px-3 py-2.5 rounded-xl border border-stone-200 bg-white text-sm text-stone-600 focus:outline-none focus:border-primary cursor-pointer">
          <option>All</option>
          {statusOptions.map((s) => <option key={s}>{s}</option>)}
        </select>
      </div>

      {/* Loading */}
      {loading && (
        <div className="flex items-center justify-center py-16">
          <i className="ri-loader-4-line animate-spin text-primary text-2xl"></i>
        </div>
      )}

      {/* Table */}
      {!loading && (
        <div className="bg-white rounded-2xl border border-stone-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-stone-100 bg-stone-50">
                  <th className="text-left px-6 py-3.5 text-xs font-semibold text-stone-500 uppercase tracking-wider">Applicant</th>
                  <th className="text-left px-4 py-3.5 text-xs font-semibold text-stone-500 uppercase tracking-wider hidden md:table-cell">Type</th>
                  <th className="text-left px-4 py-3.5 text-xs font-semibold text-stone-500 uppercase tracking-wider hidden lg:table-cell">Area of Interest</th>
                  <th className="text-left px-4 py-3.5 text-xs font-semibold text-stone-500 uppercase tracking-wider hidden lg:table-cell">Date</th>
                  <th className="text-left px-4 py-3.5 text-xs font-semibold text-stone-500 uppercase tracking-wider">Status</th>
                  <th className="text-right px-6 py-3.5 text-xs font-semibold text-stone-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-50">
                {filtered.map((a) => (
                  <tr key={a.id} className="hover:bg-stone-50/50 transition-colors duration-150">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 flex items-center justify-center bg-stone-100 rounded-full shrink-0">
                          <span className="text-xs font-bold text-stone-600">
                            {a.name.split(" ").slice(0, 2).map((n: string) => n[0]).join("")}
                          </span>
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-stone-800">{a.name}</p>
                          <p className="text-xs text-stone-400">{a.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4 hidden md:table-cell">
                      <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${a.type === "Internship" ? "bg-amber-100 text-amber-700" : "bg-rose-100 text-rose-600"}`}>
                        {a.type}
                      </span>
                    </td>
                    <td className="px-4 py-4 hidden lg:table-cell">
                      <span className="text-sm text-stone-600">{a.area}</span>
                    </td>
                    <td className="px-4 py-4 hidden lg:table-cell">
                      <span className="text-sm text-stone-400">{formatDate(a.date)}</span>
                    </td>
                    <td className="px-4 py-4">
                      <select
                        value={a.status}
                        onChange={(e) => updateStatus(a.id, e.target.value)}
                        className={`px-2.5 py-1 rounded-full text-xs font-semibold cursor-pointer border-0 focus:outline-none focus:ring-1 focus:ring-primary ${statusColors[a.status]}`}
                      >
                        {statusOptions.map((s) => <option key={s}>{s}</option>)}
                      </select>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-end gap-2">
                        <button onClick={() => setViewApp(a)} className="w-8 h-8 flex items-center justify-center text-stone-400 hover:text-primary hover:bg-primary/10 rounded-lg cursor-pointer transition-all duration-200">
                          <i className="ri-eye-line text-sm"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {filtered.length === 0 && (
              <div className="text-center py-16">
                <div className="w-12 h-12 flex items-center justify-center bg-stone-100 rounded-full mx-auto mb-3">
                  <i className="ri-file-list-3-line text-stone-400 text-xl"></i>
                </div>
                <p className="text-stone-500 text-sm font-medium">No applications found</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* View Application Modal */}
      {viewApp && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
          <div className="bg-white rounded-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-5 border-b border-stone-100">
              <h2 className="text-base font-bold text-stone-800">Application Details</h2>
              <button onClick={() => setViewApp(null)} className="w-8 h-8 flex items-center justify-center text-stone-400 hover:text-stone-600 cursor-pointer">
                <i className="ri-close-line text-lg"></i>
              </button>
            </div>
            <div className="px-6 py-5">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-14 h-14 flex items-center justify-center bg-primary/10 rounded-full shrink-0">
                  <span className="text-lg font-bold text-primary">
                    {viewApp.name.split(" ").slice(0, 2).map((n: string) => n[0]).join("")}
                  </span>
                </div>
                <div>
                  <h3 className="text-base font-bold text-stone-800">{viewApp.name}</h3>
                  <p className="text-sm text-stone-500">{viewApp.country || "—"}</p>
                  <div className="flex items-center gap-2 mt-1">
                    <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${viewApp.type === "Internship" ? "bg-amber-100 text-amber-700" : "bg-rose-100 text-rose-600"}`}>
                      {viewApp.type}
                    </span>
                    <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${statusColors[viewApp.status]}`}>
                      {viewApp.status}
                    </span>
                  </div>
                </div>
              </div>
              <div className="flex flex-col gap-3 mb-5">
                {[
                  { icon: "ri-mail-line", label: "Email", value: viewApp.email },
                  { icon: "ri-briefcase-line", label: "Area of Interest", value: viewApp.area },
                  ...(viewApp.availability ? [{ icon: "ri-calendar-line", label: "Availability", value: viewApp.availability }] : []),
                  { icon: "ri-calendar-event-line", label: "Applied On", value: formatDate(viewApp.date) },
                ].map((item) => (
                  <div key={item.label} className="flex items-start gap-3 p-3 bg-stone-50 rounded-xl">
                    <div className="w-7 h-7 flex items-center justify-center bg-white rounded-lg shrink-0 mt-0.5">
                      <i className={`${item.icon} text-stone-500 text-sm`}></i>
                    </div>
                    <div>
                      <p className="text-xs text-stone-400">{item.label}</p>
                      <p className="text-sm font-medium text-stone-700">{item.value}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div className="bg-stone-50 rounded-xl p-4 mb-5">
                <p className="text-xs font-semibold text-stone-500 uppercase tracking-wider mb-2">Message</p>
                <p className="text-sm text-stone-700 leading-relaxed">{viewApp.message || "No message provided."}</p>
              </div>
              <div>
                <p className="text-xs font-semibold text-stone-500 uppercase tracking-wider mb-2">Update Status</p>
                <div className="flex flex-wrap gap-2">
                  {statusOptions.map((s) => (
                    <button
                      key={s}
                      onClick={() => updateStatus(viewApp.id, s)}
                      className={`px-3 py-1.5 rounded-full text-xs font-semibold cursor-pointer transition-all duration-200 ${viewApp.status === s ? statusColors[s] + " ring-2 ring-offset-1 ring-current" : "bg-stone-100 text-stone-500 hover:bg-stone-200"}`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}